import Quill from 'quill';

const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'courier', 'times-new-roman', 'monospace'];
Quill.register(Font, true);

/**
 * Inline style colors so saved HTML works in the mobile WebView.
 * Register by explicit path with overwrite=true so we replace Quill's default.
 *
 * Quill drops black/#000000 as "default" — force it to stay as inline style.
 */
const ColorStyle = Quill.import('attributors/style/color');
const BackgroundStyle = Quill.import('attributors/style/background');

ColorStyle.add = (node: HTMLElement, value: string) => {
  if (!value) {
    node.style.color = '';
    return;
  }
  const isBlack =
    value === 'black' ||
    value === '#000000' ||
    value === '#000' ||
    value === 'rgb(0, 0, 0)' ||
    value === 'rgb(0,0,0)';
  node.style.color = isBlack ? '#000000' : value;
  return true;
};

Quill.register('formats/color', ColorStyle, true);
Quill.register('formats/background', BackgroundStyle, true);
Quill.register('attributors/style/color', ColorStyle, true);
Quill.register('attributors/style/background', BackgroundStyle, true);

/** Map Quill class colors → inline styles (for older saved HTML). */
export const QUILL_NAMED_COLORS: Record<string, string> = {
  black: '#000000',
  red: '#e60000',
  orange: '#f90',
  yellow: '#ff0',
  green: '#008a00',
  blue: '#06c',
  purple: '#93f',
  white: '#ffffff',
};

/**
 * Convert ql-color-* / ql-bg-* classes into inline style attributes so
 * colors render outside the Quill editor (CMS preview, mobile WebView).
 */
export function normalizeQuillColorHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return html;
  }

  let result = html;

  Object.entries(QUILL_NAMED_COLORS).forEach(([name, hex]) => {
    const colorClassRe = new RegExp(
      `(<[a-z][a-z0-9]*\\b[^>]*?)\\sclass=(["'])([^"']*\\bql-color-${name}\\b[^"']*)\\2([^>]*>)`,
      'gi'
    );
    result = result.replace(colorClassRe, (_m, before, quote, classes, after) => {
      const remaining = classes
        .split(/\s+/)
        .filter((c: string) => c && c !== `ql-color-${name}`)
        .join(' ');
      const classAttr = remaining ? ` class=${quote}${remaining}${quote}` : '';
      if (/\sstyle=/i.test(before) || /\sstyle=/i.test(after)) {
        return `${before}${classAttr}${after}`.replace(
          /\sstyle=(["'])/i,
          ` style=$1color: ${hex}; `
        );
      }
      return `${before}${classAttr} style="color: ${hex};"${after}`;
    });

    const bgClassRe = new RegExp(
      `(<[a-z][a-z0-9]*\\b[^>]*?)\\sclass=(["'])([^"']*\\bql-bg-${name}\\b[^"']*)\\2([^>]*>)`,
      'gi'
    );
    result = result.replace(bgClassRe, (_m, before, quote, classes, after) => {
      const remaining = classes
        .split(/\s+/)
        .filter((c: string) => c && c !== `ql-bg-${name}`)
        .join(' ');
      const classAttr = remaining ? ` class=${quote}${remaining}${quote}` : '';
      if (/\sstyle=/i.test(before) || /\sstyle=/i.test(after)) {
        return `${before}${classAttr}${after}`.replace(
          /\sstyle=(["'])/i,
          ` style=$1background-color: ${hex}; `
        );
      }
      return `${before}${classAttr} style="background-color: ${hex};"${after}`;
    });
  });

  return result;
}
